import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Row, Col, Icon } from "antd";
import QueryString from "query-string";
import { InboxCasesTable } from "templates";
import { StageTabsGenerator } from "hocs";
import {
    fetchCases,
    fetchStageCase,
    fetchBucketCases,
    bulkAssignBucketCases,
    downloadCasesXlsx,
    fetchCasesCount
} from "store/actions";
import { isFoundersLogin } from "../components/helper";
import "styles/inbox-greeting.css";

const StageTabs = StageTabsGenerator(InboxCasesTable);

// Pick the right greeting for local time. IST users see morning until noon,
// afternoon until 5pm, then evening — matches the phrasing the credit team
// uses in their own hand-offs.
function greetingForHour(hour) {
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
}

function GreetingBanner({ user }) {
    const first = (user && user.get && user.get("first_name")) || "";
    const greeting = greetingForHour(new Date().getHours());
    return (
        <div className={"inbox-greet"}>
            <div className={"inbox-greet__msg"}>
                {greeting}
                {first ? `, ${first}` : ""}
                <span className={"inbox-greet__sub"}>
                    {" "}
                    · here's what's on your plate today
                </span>
            </div>
        </div>
    );
}

GreetingBanner.propTypes = {
    user: PropTypes.object
};

// Quick-filter chip strip — sits between the stage tabs and the case table.
// Each chip pushes a `view=` (or similar) query param the existing table
// filter logic already honours. That way we get one-click filters without
// wiring a whole new filter mechanism.
const QUICK_FILTERS = [
    { id: "assigned", label: "My Cases", icon: "user" },
    { id: "unassigned", label: "Unassigned", icon: "user-delete" },
    { id: "all", label: "All", icon: "unordered-list" }
];

function QuickFilters({ history, location }) {
    const params = QueryString.parse((location && location.search) || "", {
        parseBooleans: true,
        parseNumbers: true
    });
    // Default view is "assigned" (see stage-tabs.js), so if `view` isn't in the
    // URL yet treat the "My Cases" chip as the active one.
    const active = String(params.view || "assigned");
    const apply = viewId => {
        const next = { ...params, view: viewId };
        history.push({ ...location, search: QueryString.stringify(next) });
    };
    return (
        <div className={"inbox-quick"}>
            <span className={"inbox-quick__label"}>Quick view:</span>
            {QUICK_FILTERS.map(f => (
                <button
                    key={f.id}
                    type={"button"}
                    className={
                        "inbox-quick__chip"
                        + (active === f.id ? " inbox-quick__chip--on" : "")
                    }
                    onClick={() => apply(f.id)}
                >
                    <Icon type={f.icon} /> {f.label}
                </button>
            ))}
        </div>
    );
}

QuickFilters.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object
};

class InboxView extends React.Component {
    // Extract `view` from the current URL so the badges refetch when the
    // user flips between "My Cases" and "All" chips. Defaults to "assigned"
    // (matches the same default the stage-tabs URL synth uses on mount).
    getViewFromLocation = () => {
        const search = (this.props.location && this.props.location.search) || "";
        const parsed = QueryString.parse(search, {
            parseBooleans: true,
            parseNumbers: true
        });
        return String(parsed.view || "assigned");
    };

    componentDidMount() {
        const alreadyRedirected = sessionStorage.getItem(
            "caseListingRedirected"
        );
        if (
            isFoundersLogin(this.props.user.getIn(["data", "email"]))
            && !alreadyRedirected
        ) {
            sessionStorage.setItem("caseListingRedirected", "true");
            this.props.history.replace("/app/cases/list/");
        }
        // Populate per-stage case counts so the stage bar can show queue depth
        // badges and the greeting can show the total. Passing the current
        // view means "My Cases" badges show ONLY the user's own cases (the
        // backend interprets view=assigned as this_user==me).
        if (this.props.fetchCasesCount) {
            this.props.fetchCasesCount(this.getViewFromLocation());
        }
    }

    componentDidUpdate(prevProps) {
        // The "My Cases / Unassigned / All" chip strip pushes ?view=<...>
        // into the URL. When it changes, refetch counts so the stage-bar
        // badges reflect the new filter.
        const prevSearch = (prevProps.location && prevProps.location.search) || "";
        const currSearch = (this.props.location && this.props.location.search) || "";
        if (prevSearch === currSearch) return;
        const prevView = String(
            QueryString.parse(prevSearch, { parseBooleans: true }).view
            || "assigned"
        );
        const nextView = this.getViewFromLocation();
        if (prevView !== nextView && this.props.fetchCasesCount) {
            this.props.fetchCasesCount(nextView);
        }
    }
    render() {
        const {
            fetchStageCase,
            fetchBucketCases,
            cases,
            stagesStatus,
            stages,
            user,
            bulkAssignBucketCases,
            loading,
            caseCounts
        } = this.props;
        return (
            <Row className={"h-100 inbox-page"}>
                <Col span={24} className={"h-100"}>
                    <GreetingBanner user={user.get("data")} />
                    <QuickFilters
                        history={this.props.history}
                        location={this.props.location}
                    />
                    <StageTabs
                        loading={loading}
                        stages={stages}
                        defaultTab={stages.getDefaultTab()}
                        caseStatus={"INBOX"}
                        toggleHelp={this.props.toggleHelp}
                        participatedStages={this.props.participatedStages}
                        stagesStatus={stagesStatus}
                        history={this.props.history}
                        caseCounts={caseCounts}
                        WrappedComponentProps={{
                            user: user.get("data"),
                            permissions: user.get("permissions"),
                            location: this.props.location,
                            cases,
                            stagesStatus,
                            fetchStageCase,
                            fetchBucketCases,
                            bulkAssignBucketCases,
                            size: "small",
                            handleSave: () => null,
                            editable: true,
                            onXlsxDownload: this.props.downloadCasesXlsx
                        }}
                    />
                </Col>
            </Row>
        );
    }
}

InboxView.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
    match: PropTypes.object,
    user: PropTypes.object,

    stages: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    loading: PropTypes.bool,

    cases: PropTypes.object,
    stagesStatus: PropTypes.string,

    fetchStageCase: PropTypes.func,
    fetchBucketCases: PropTypes.func,

    participatedStages: PropTypes.object,
    toggleHelp: PropTypes.bool,
    bulkAssignBucketCases: PropTypes.func,
    downloadCasesXlsx: PropTypes.func,
    caseCounts: PropTypes.object,
    fetchCasesCount: PropTypes.func
};

const mapStateToProps = state => ({
    cases: state.cases,
    loading: !state.stagePermissions.isDataFetched(),
    stages: state.stagePermissions.update("data", data =>
        data.filter(item => ![55, 4, 18, 20, 19, 34, 6].includes(item.id))
    ),
    participatedStages: state.stages.get("data"),
    stagesStatus: state.stages.get("status"),
    user: state.user,
    toggleHelp: state.toggleHelp.get("value"),
    // caseCount saga (case-count-saga.js) populates state.caseCount with an
    // Immutable Map keyed by bucket ("INBOX" / "PARTICIPATED"), each of whose
    // "data" is a Map keyed by stage_name -> count.
    caseCounts: state.caseCount.getIn(["INBOX", "data"])
});
const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            fetchCases,
            fetchStageCase,
            fetchBucketCases,
            bulkAssignBucketCases,
            downloadCasesXlsx,
            fetchCasesCount
        },
        dispatch
    );

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InboxView);
